<script setup>
import {
  computed,
  reactive,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useCompaniesStore } from 'dashboard/stores/companies';

const props = defineProps({
  company: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
});

const { t } = useI18n();
const companiesStore = useCompaniesStore();
const { isWorkspaceV2 } = useWorkspaceV2();
const nameLabel = computed(() => t('COMPANIES.DETAIL.PROFILE.FIELDS.NAME'));
const domainLabel = computed(() => t('COMPANIES.DETAIL.PROFILE.FIELDS.DOMAIN'));
const saveError = ref('');
const avatarError = ref('');
const saving = ref(false);
const discardRef = ref(null);
const savedState = ref('');

const form = reactive({ name: '', domain: '', description: '' });
const avatarPreviewUrl = ref('');
const isUploadingAvatar = ref(false);

const uiFlags = computed(() => companiesStore.getUIFlags);
const isUpdating = computed(() => uiFlags.value.updatingItem);
const isAvatarBusy = computed(
  () =>
    isUploadingAvatar.value || uiFlags.value.deletingAvatar || isUpdating.value
);

const displayName = computed(
  () => props.company?.name || t('COMPANIES.UNNAMED')
);
const avatarSource = computed(
  () => avatarPreviewUrl.value || props.company?.avatarUrl || ''
);
const isFormInvalid = computed(() => !form.name.trim());
const hasChanges = computed(() => JSON.stringify(form) !== savedState.value);

const summary = computed(() => {
  const { createdAt, lastActivityAt } = props.company || {};
  return [
    createdAt &&
      t('COMPANIES.DETAIL.PROFILE.CREATED_AT', {
        date: dynamicTime(createdAt),
      }),
    lastActivityAt &&
      t('COMPANIES.DETAIL.PROFILE.LAST_ACTIVE', {
        date: dynamicTime(lastActivityAt),
      }),
  ]
    .filter(Boolean)
    .join(' • ');
});

const syncForm = company => {
  form.name = company?.name || '';
  form.domain = company?.domain || '';
  form.description = company?.description || '';
  savedState.value = JSON.stringify(form);
};

const isCurrentCompany = companyId => Number(props.company?.id) === companyId;

watch(
  () => [
    props.company?.id,
    props.company?.name,
    props.company?.domain,
    props.company?.description,
  ],
  (next, previous) => {
    if (next[0] !== previous?.[0]) {
      saveError.value = '';
      avatarError.value = '';
      avatarPreviewUrl.value = '';
      syncForm(props.company);
    } else if (!hasChanges.value || !isWorkspaceV2.value) {
      syncForm(props.company);
    }
  },
  { immediate: true }
);

const handleAvatarUpload = async ({ file, url }) => {
  if (isAvatarBusy.value || saving.value) return;
  avatarError.value = '';
  avatarPreviewUrl.value = url;
  isUploadingAvatar.value = true;
  try {
    await companiesStore.update({ id: props.company.id, avatar: file });
    useAlert(t('COMPANIES.DETAIL.AVATAR.UPLOAD_SUCCESS'));
  } catch {
    avatarPreviewUrl.value = '';
    avatarError.value = t('COMPANIES.DETAIL.AVATAR.UPLOAD_ERROR');
    useAlert(avatarError.value);
  } finally {
    isUploadingAvatar.value = false;
  }
};

const handleAvatarDelete = async () => {
  if (isAvatarBusy.value || saving.value) return;
  avatarError.value = '';
  try {
    await companiesStore.deleteCompanyAvatar(props.company.id);
    avatarPreviewUrl.value = '';
    useAlert(t('COMPANIES.DETAIL.AVATAR.DELETE_SUCCESS'));
  } catch {
    avatarError.value = t('COMPANIES.DETAIL.AVATAR.DELETE_ERROR');
    useAlert(avatarError.value);
  }
};

const handleUpdateCompany = async () => {
  if (
    saving.value ||
    isAvatarBusy.value ||
    isFormInvalid.value ||
    !hasChanges.value
  )
    return;
  saving.value = true;
  saveError.value = '';
  const companyId = Number(props.company.id);

  try {
    const updated = await companiesStore.update({
      id: companyId,
      name: form.name.trim(),
      domain: form.domain.trim() || null,
      description: form.description.trim() || null,
    });
    if (!isCurrentCompany(companyId)) return;

    syncForm(updated);
    useAlert(t('COMPANIES.DETAIL.PROFILE.MESSAGES.UPDATE_SUCCESS'));
  } catch {
    if (!isCurrentCompany(companyId)) return;

    if (!isWorkspaceV2.value) syncForm(props.company);
    saveError.value = t('COMPANIES.DETAIL.PROFILE.MESSAGES.UPDATE_ERROR');
    useAlert(saveError.value);
  } finally {
    saving.value = false;
  }
};
let resolveNavigation = null;
const finishNavigation = allowed => {
  const resolve = resolveNavigation;
  resolveNavigation = null;
  if (allowed) savedState.value = JSON.stringify(form);
  if (resolve) discardRef.value?.close();
  resolve?.(allowed);
};
const guardNavigation = (to, from) => {
  if (!isWorkspaceV2.value) return true;
  if (
    to?.name === from?.name &&
    to?.params?.companyId === from?.params?.companyId &&
    to?.params?.accountId === from?.params?.accountId
  )
    return true;
  if (saving.value || isAvatarBusy.value) return false;
  if (!hasChanges.value) return true;
  resolveNavigation?.(false);
  return new Promise(resolve => {
    resolveNavigation = resolve;
    discardRef.value?.open();
  });
};
onBeforeRouteLeave(guardNavigation);
onBeforeRouteUpdate(guardNavigation);
const beforeUnload = event => {
  if (!isWorkspaceV2.value || !hasChanges.value) return;
  event.preventDefault();
  event.returnValue = '';
};
onMounted(() => window.addEventListener('beforeunload', beforeUnload));
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', beforeUnload);
  resolveNavigation?.(false);
});
</script>

<template>
  <div v-if="isLoading && !company?.id" class="text-sm text-n-slate-11">
    {{ t('COMPANIES.DETAIL.LOADING') }}
  </div>

  <div v-else-if="company?.id" class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :name="displayName"
        :src="avatarSource"
        :size="72"
        :allow-upload="!isAvatarBusy && !saving"
        hide-offline-status
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />

      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ displayName }}
        </h3>
        <span class="text-sm leading-6 text-n-slate-11">{{ summary }}</span>
        <p
          v-if="isUploadingAvatar || uiFlags.deletingAvatar"
          class="text-sm text-n-slate-11"
        >
          {{ t('COMPANIES.DETAIL.AVATAR.UPDATING') }}
        </p>
      </div>
    </div>

    <p
      v-if="isWorkspaceV2 && avatarError"
      role="alert"
      class="text-sm text-n-ruby-11"
    >
      {{ avatarError }}
    </p>
    <div
      class="flex flex-col items-start w-full gap-6"
      :class="{
        'rounded-xl border border-n-weak bg-n-solid-1 p-4 sm:p-5 [&_input]:!h-11 [&_input]:!rounded-xl [&_textarea]:!rounded-xl [&_button]:min-h-11 [&_button]:!rounded-xl':
          isWorkspaceV2,
      }"
    >
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('COMPANIES.DETAIL.PROFILE.TITLE') }}
      </span>

      <div class="grid w-full gap-4 sm:grid-cols-2">
        <div class="flex min-w-0 flex-col gap-1.5">
          <Input
            id="company-profile-name"
            v-model="form.name"
            :label="isWorkspaceV2 ? nameLabel : ''"
            :placeholder="t('COMPANIES.DETAIL.PROFILE.FIELDS.NAME')"
            :disabled="isUpdating || saving"
            custom-input-class="h-8 !pt-1 !pb-1"
          />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <Input
            id="company-profile-domain"
            v-model="form.domain"
            :label="isWorkspaceV2 ? domainLabel : ''"
            :placeholder="t('COMPANIES.DETAIL.PROFILE.FIELDS.DOMAIN')"
            :disabled="isUpdating || saving"
            custom-input-class="h-8 !pt-1 !pb-1"
          />
        </div>
      </div>

      <TextArea
        v-model="form.description"
        :label="
          isWorkspaceV2
            ? t('COMPANIES.DETAIL.PROFILE.DESCRIPTION_PLACEHOLDER')
            : ''
        "
        :placeholder="t('COMPANIES.DETAIL.PROFILE.DESCRIPTION_PLACEHOLDER')"
        :disabled="isUpdating || saving"
        :max-length="280"
        class="w-full"
        show-character-count
        auto-height
      />

      <p
        v-if="isWorkspaceV2 && saveError"
        role="alert"
        class="text-sm text-n-ruby-11"
      >
        {{ saveError }}
      </p>
      <Button
        :label="t('COMPANIES.DETAIL.PROFILE.ACTIONS.SAVE')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="saving || isUpdating || isFormInvalid || !hasChanges"
        @click="handleUpdateCompany"
      />
    </div>
    <Dialog
      ref="discardRef"
      type="alert"
      :title="t('WORKSPACE_V2.COMPANY_DETAIL.DISCARD_TITLE')"
      :description="t('WORKSPACE_V2.COMPANY_DETAIL.DISCARD_DESCRIPTION')"
      :confirm-button-label="t('WORKSPACE_V2.TASK_EDITOR.DISCARD')"
      :cancel-button-label="t('WORKSPACE_V2.TASK_EDITOR.KEEP_EDITING')"
      @confirm="finishNavigation(true)"
      @close="finishNavigation(false)"
    />
  </div>
</template>
